import axios from 'axios';
import { API_LEAD_SERVICE_CONFIG } from '../config/api.config';
import toast from 'react-hot-toast';

/**
 * HTTP Client for Public Link-Based Forms
 * This client handles authentication differently from the main httpClientLeadService:
 * 
 * 1. Initial form load: No auth required (public endpoint)
 * 2. After receiving token: Stores and uses link-based token
 * 3. Dropdown API calls: Automatically includes stored token
 */

// Global promise to track ongoing refresh requests for link-based tokens
let linkRefreshPromise = null;

// Flag to prevent multiple session expiry toasts for link-based forms
let isLinkSessionExpiredToastShown = false;

// Function to reset the session expired flag (useful when loading a new form)
export const resetLinkSessionExpiredFlag = () => {
  isLinkSessionExpiredToastShown = false;
};

const publicFormClient = axios.create({
  baseURL: API_LEAD_SERVICE_CONFIG.BASE_LEAD_SERVICE_URL,
  timeout: API_LEAD_SERVICE_CONFIG.TIMEOUT,
  headers: API_LEAD_SERVICE_CONFIG.HEADERS,
});

// Request interceptor - adds link-based token if available
publicFormClient.interceptors.request.use(
  (config) => {
    // Check for link-based token in sessionStorage
    const linkToken = sessionStorage.getItem('link_access_token');
    
    if (linkToken) {
      config.headers.Authorization = `Bearer ${linkToken}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handles errors
publicFormClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If no config exists (network error, etc.), reject immediately
    if (!originalRequest) {
      console.error('Network or configuration error:', error.message);
      toast.error('Network error. Please check your connection and try again.');
      return Promise.reject(error);
    }

    // Helper function to show session expiry toast only once
    const handleLinkSessionExpiry = () => {
      if (!isLinkSessionExpiredToastShown) {
        // Only show toast if we actually had a token (i.e., user was authenticated)
        const hadToken = sessionStorage.getItem('link_access_token') || sessionStorage.getItem('link_refresh_token');
        if (hadToken) {
          isLinkSessionExpiredToastShown = true;
          toast.error('Your session has expired. Please use a new link.');
        }
        clearLinkBasedToken();
        linkRefreshPromise = null;
      }
    };

    // Skip refresh logic for the refresh endpoint itself to prevent infinite loops
    if (originalRequest.url?.includes('/v1/auth/refresh')) {
      handleLinkSessionExpiry();
      return Promise.reject(error);
    }

    // Handle 401 - Token expired for link-based forms
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = sessionStorage.getItem('link_refresh_token');
      const accessToken = sessionStorage.getItem('link_access_token');
      
      // If we never had tokens, this is not a session expiry - just a regular 401
      // (e.g., accessing a protected endpoint before authentication)
      if (!refreshToken && !accessToken) {
        // Don't show session expired toast, just reject silently
        return Promise.reject(error);
      }
      
      if (!refreshToken) {
        handleLinkSessionExpiry();
        return Promise.reject(error);
      }

      try {
        // Check if a refresh is already in progress
        if (!linkRefreshPromise) {
          // No refresh in progress, start a new one
          linkRefreshPromise = axios.post(
            `${API_LEAD_SERVICE_CONFIG.BASE_LEAD_SERVICE_URL}/v1/auth/refresh`,
            { refresh_token: refreshToken }
          );
        }

        // Wait for the refresh to complete (either the one we started or one already in progress)
        const response = await linkRefreshPromise;
        
        const tokenData = response.data.data?.[0];
        const newAccessToken = tokenData?.access_token;
        const newRefreshToken = tokenData?.refresh_token;
        
        if (newAccessToken) {
          // Update stored tokens
          sessionStorage.setItem('link_access_token', newAccessToken);
          if (newRefreshToken) {
            sessionStorage.setItem('link_refresh_token', newRefreshToken);
          }
          
          // Clear the refresh promise after successful refresh
          linkRefreshPromise = null;
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return publicFormClient(originalRequest);
        } else {
          throw new Error('No access token received from refresh');
        }
      } catch (refreshError) {
        // Refresh token failed or expired
        handleLinkSessionExpiry();
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

/**
 * Store link-based token for subsequent requests
 * Call this after receiving the initial form load response
 */
export const setLinkBasedToken = (accessToken, refreshToken) => {
  sessionStorage.setItem('link_access_token', accessToken);
  if (refreshToken) {
    sessionStorage.setItem('link_refresh_token', refreshToken);
  }
  };

/**
 * Clear link-based tokens (call when leaving the form)
 */
export const clearLinkBasedToken = () => {
  sessionStorage.removeItem('link_access_token');
  sessionStorage.removeItem('link_refresh_token');
  };

export default publicFormClient;

